import { useTranslation } from "react-i18next";

import {
  ArrowRightIcon,
  CircleAlertIcon,
  CircleCheckIcon,
  CircleDashedIcon,
} from "lucide-react";

import {
  type SubmissionChecklistSection,
  getChecklistIssueText,
} from "simplydo/core/submissibility";
import { styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import { Card, CardContent } from "simplydo/react/ui/card";
import { Icon } from "simplydo/react/ui/icon";

import { SUBMISSION_CHECKLIST_ID } from "../submissionChecklist";

const ChecklistAnchor = styled.section`
  margin-bottom: 16px;
  border-radius: 6px;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.primaryColour};
    outline-offset: 2px;
  }
`;

const ChecklistHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`;

const ChecklistTitle = styled.h4`
  margin: 0;
  font-size: 1.1em;
`;

const ChecklistInfo = styled.p`
  margin: 0 0 12px;
  opacity: 0.75;
`;

const ChecklistSectionName = styled.div`
  font-weight: 700;
  margin: 12px 0 4px;
`;

const ChecklistList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const ChecklistRow = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  min-height: 32px;

  &.is-optional {
    opacity: 0.7;
  }

  > .row-text {
    flex: 1;
    min-width: 0;
    word-break: break-word;
  }

  > .row-text > .row-issue {
    opacity: 0.7;
    margin-left: 6px;
  }

  > ${Button} {
    flex-shrink: 0;
  }
`;

const BlockingIcon = styled(Icon)`
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.red.ink};
`;

const OptionalIcon = styled(Icon)`
  flex-shrink: 0;
`;

const CompleteIcon = styled(Icon)`
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.green.ink};
`;

type SubmissionChecklistProps = {
  checklist: SubmissionChecklistSection[];
  isSubmissible: boolean;
  goToField: (fieldId: string, sectionScrollKey: string) => void;
  goToSection: (sectionScrollKey: string) => void;
};

const SubmissionChecklist = ({
  checklist,
  isSubmissible,
  goToField,
  goToSection,
}: SubmissionChecklistProps) => {
  const { t } = useTranslation();

  if (checklist.length === 0) {
    if (!isSubmissible) {
      return null;
    }
    return (
      <ChecklistAnchor id={SUBMISSION_CHECKLIST_ID} tabIndex={-1}>
        <Card fluid>
          <CardContent>
            <ChecklistHeader>
              <CompleteIcon icon={CircleCheckIcon} />
              <span>{t("ideas.submissionChecklist.allComplete")}</span>
            </ChecklistHeader>
          </CardContent>
        </Card>
      </ChecklistAnchor>
    );
  }

  return (
    <ChecklistAnchor
      id={SUBMISSION_CHECKLIST_ID}
      tabIndex={-1}
      aria-labelledby={`${SUBMISSION_CHECKLIST_ID}-title`}
    >
      <Card fluid>
        <CardContent>
          <ChecklistHeader>
            <ChecklistTitle id={`${SUBMISSION_CHECKLIST_ID}-title`}>
              {isSubmissible
                ? t("ideas.submissionChecklist.readyTitle")
                : t("ideas.submissionChecklist.title")}
            </ChecklistTitle>
          </ChecklistHeader>
          <ChecklistInfo>
            {isSubmissible
              ? t("ideas.submissionChecklist.readyInfo")
              : t("ideas.submissionChecklist.info")}
          </ChecklistInfo>
          {checklist.map((section) => (
            <div key={section.key}>
              <ChecklistSectionName>{section.name}</ChecklistSectionName>
              <ChecklistList>
                {section.blockingFields.map((field, index) => {
                  const issueText = getChecklistIssueText(field, t);
                  return (
                    <ChecklistRow key={field.id ?? `${section.key}-${index}`}>
                      <BlockingIcon icon={CircleAlertIcon} />
                      <span className="row-text">
                        {field.title}
                        {issueText ? (
                          <span className="row-issue">{issueText}</span>
                        ) : null}
                      </span>
                      <Button
                        size="small"
                        variant="ghost"
                        onClick={() =>
                          field.id
                            ? goToField(field.id, section.scrollKey)
                            : goToSection(section.scrollKey)
                        }
                      >
                        {field.id
                          ? t("ideas.submissionChecklist.goToField")
                          : t("ideas.submissionChecklist.goToSection")}
                        <Icon icon={ArrowRightIcon} />
                      </Button>
                    </ChecklistRow>
                  );
                })}
                {section.optionalRemaining > 0 ? (
                  <ChecklistRow className="is-optional">
                    <OptionalIcon icon={CircleDashedIcon} />
                    <span className="row-text">
                      {t("ideas.submissionChecklist.optionalRemaining", {
                        count: section.optionalRemaining,
                      })}
                    </span>
                    <Button
                      size="small"
                      variant="ghost"
                      onClick={() => goToSection(section.scrollKey)}
                    >
                      {t("ideas.submissionChecklist.goToSection")}
                      <Icon icon={ArrowRightIcon} />
                    </Button>
                  </ChecklistRow>
                ) : null}
              </ChecklistList>
            </div>
          ))}
        </CardContent>
      </Card>
    </ChecklistAnchor>
  );
};

export default SubmissionChecklist;
